import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import DefaultAccordion from "@/features/Common/components/DefaultAccordion";
import FullscreenBackground from "@/features/Common/components/FullscreenBackground";
import IconLoader from "@/features/Common/components/IconLoader";
import InfoContextPanel from "@/features/Common/components/InfoContextPanel";
import StepsCounter from "@/features/Common/components/StepsCounter";
import WatermarkIcon from "@/features/Common/components/WatermarkIcon";
import { trackPageViewV2 } from "@/services/analytics-adapter";
import { VoucherVendorDefinitionId } from "@/services/core-api-adapter";
import theme from "@/theme";
import { convertPxToRem } from "@/utils";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import MedicalConsultationPartnerMoreInfoModal from "../../../MedicalConsultations/components/MedicalConsultationPartnerMoreInfoModal";

interface BuyVoucherPromptProps {
  voucherVendorDefinitionId: string;
  onContinueButtonClick: () => void;
  onBackButtonClick: () => void;
}

export function BuyVoucherPrompt({
  voucherVendorDefinitionId,
  onContinueButtonClick,
  onBackButtonClick,
}: BuyVoucherPromptProps) {
  const { t } = useTranslation();
  const [height, setHeight] = useState(0);
  const [transformedVoucherSubType, setTransformedVoucherSubType] =
    useState<string>("");

  function transformVoucherSubTypeForTranslationKeys(
    voucherVendorDefinitionId: string
  ) {
    if (voucherVendorDefinitionId === VoucherVendorDefinitionId.NURSE_CONSULT) {
      return setTransformedVoucherSubType("NurseVoucher");
    } else if (
      voucherVendorDefinitionId === VoucherVendorDefinitionId.DOCTOR_CONSULT
    ) {
      return setTransformedVoucherSubType("DoctorVoucher");
    } else {
      return setTransformedVoucherSubType("BloodTestVoucher");
    }
  }

  useEffect(() => {
    transformVoucherSubTypeForTranslationKeys(voucherVendorDefinitionId);
  }, [voucherVendorDefinitionId]);

  useEffect(() => {
    trackPageViewV2({
      pageName: `${transformedVoucherSubType} prompt`,
      pageSubSection1: transformedVoucherSubType,
      pageSubSection2: `${transformedVoucherSubType}: Next steps`,
      pageCategory: "Vouchers",
    });
  }, []);

  function onInfoIconClick() {
    setIsInfoModalOpen(true);
  }

  function onMoreInfoModalClose() {
    setIsInfoModalOpen(false);
  }

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  return (
    <>
      <Stack p={2} justifyContent="space-between" flexGrow={1} minHeight="100%">
        <Stack
          color="neutral.50"
          sx={{
            height: "100%",
          }}
        >
          <Stack spacing={3}>
            <Stack
              direction="row"
              alignSelf="center"
              alignItems="center"
              spacing={2.5}
              p={2}
            >
              <WatermarkIcon
                height={36}
                width={36}
                color={"neutral.50"}
                foreground="neutral.50"
                Icon={"FaceIconBackground"}
                iconSize="large"
              />

              <IconLoader
                icon="ArrowNextIcon"
                sx={{
                  fontSize: convertPxToRem(25),
                  alignSelf: "center",
                  color: "neutral.50",
                }}
              />
              <Box
                src={`/assets/MedicalAssistancePartners/Intercare/logo.svg`}
                component="img"
                sx={{
                  zIndex: 2,
                }}
                alt={t("VoucherFlow.screens.animationAlternateText")}
              />

              <ButtonWithAnalytics
                page="Voucher prompt"
                text={t("MedicalConsultationPartner.more.info.modal.title")}
                intent="informational"
                variant="text"
                aria-label={t(
                  "MedicalConsultationPartner.more.info.modal.title"
                )}
              >
                <IconLoader
                  icon="InformationSymbolInCircleIcon"
                  color="info"
                  onClick={onInfoIconClick}
                  sx={{ fontSize: convertPxToRem(20), cursor: "pointer" }}
                />
              </ButtonWithAnalytics>
            </Stack>

            <InfoContextPanel
              title={t("InfoContextPanel.vouchers.title")}
              icon="CloudStatusIcon"
              text={t(
                `InfoContextPanel.vouchers.${transformedVoucherSubType}.subtitle` as any
              )}
            />

            <Stack spacing={2}>
              <Stack
                bgcolor="neutral.50"
                borderRadius={theme.shape.borderRadius}
                p={4}
                spacing={2}
              >
                <Trans>
                  <Typography variant="h2">
                    {t(`${transformedVoucherSubType}.stepsPanel.title` as any)}
                  </Typography>
                </Trans>

                <Stack direction="row">
                  <Stack
                    sx={{
                      paddingTop:
                        transformedVoucherSubType === "BloodTestVoucher"
                          ? 4
                          : 0,
                    }}
                  >
                    <StepsCounter stepCounter={1} />
                  </Stack>
                  <Stack>
                    {transformedVoucherSubType === "BloodTestVoucher" ? (
                      <DefaultAccordion
                        title={t(
                          `${transformedVoucherSubType}.stepsPanel.step1Label`
                        )}
                        aria-label={t(
                          `${transformedVoucherSubType}.stepsPanel.step1Label`
                        )}
                        key={t(
                          `${transformedVoucherSubType}.stepsPanel.step1Label`
                        )}
                        onExpand={() => {
                          if (height === 0) {
                            setHeight(1);
                          } else {
                            setTimeout(() => {
                              setHeight(0);
                            }, 200);
                          }
                        }}
                        accordionStyles={{
                          textTransform: "capitalize",
                          boxShadow: "none",
                          width: "100%",
                          height: height,
                          padding: "0",
                          paddingTop: 2,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between">
                          <Typography>Ampath fee</Typography>
                          <Typography color="primary">R377</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography>Unu platform fee</Typography>
                          <Typography color="primary">R42</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography>Unu transaction fee</Typography>
                          <Typography color="primary">R9</Typography>
                        </Stack>
                      </DefaultAccordion>
                    ) : (
                      <Typography ml={2} variant="body1">
                        <Trans
                          i18nKey={
                            `${transformedVoucherSubType}.stepsPanel.step1Label` as any
                          }
                        ></Trans>
                      </Typography>
                    )}
                  </Stack>
                </Stack>

                <Stack direction="row">
                  <Stack>
                    <StepsCounter stepCounter={2} />
                  </Stack>
                  <Stack>
                    <Stack>
                      <Typography ml={2} variant="body1">
                        <Trans
                          i18nKey={
                            `${transformedVoucherSubType}.stepsPanel.step2Label` as any
                          }
                        ></Trans>
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction="row">
                  <Stack>
                    <StepsCounter stepCounter={3} />
                  </Stack>
                  <Stack>
                    <Typography ml={2} variant="body1">
                      <Trans
                        i18nKey={
                          `${transformedVoucherSubType}.stepsPanel.step3Label` as any
                        }
                      ></Trans>
                    </Typography>
                    {(transformedVoucherSubType === "DoctorVoucher" ||
                      transformedVoucherSubType === "NurseVoucher") && (
                      <Typography
                        ml={2}
                        component="p"
                        sx={{ fontSize: convertPxToRem(16) }}
                      >
                        <Trans
                          i18nKey={
                            `${transformedVoucherSubType}.stepsPanel.disclaimer` as any
                          }
                        ></Trans>
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            <Typography textAlign="center" color="info">
              {t("DirectToDoctor.prompt.footer.title")}
            </Typography>

            <Stack direction="row" spacing={2}>
              <Button
                onClick={onBackButtonClick}
                color="info"
                fullWidth
                variant="outlined"
              >
                {t("common.backButton")}
              </Button>
              <Button onClick={onContinueButtonClick} color="info" fullWidth>
                {t("common.continueButton")}
              </Button>
            </Stack>
          </Stack>
        </Stack>
        <FullscreenBackground color="primary.main" />
      </Stack>
      <MedicalConsultationPartnerMoreInfoModal
        isOpen={isInfoModalOpen}
        onClose={onMoreInfoModalClose}
      />
    </>
  );
}
